/**
 * Query-string serialization in the shape the v3 API reads: bracket nesting for
 * objects (`filter[station_id]`), `[]` for arrays, `1`/`0` for booleans.
 *
 * `null` is sent as an empty value because v3 column filters read an empty
 * value as IS NULL (the "no value" group). `undefined` is dropped. Keeping this
 * in the SDK, rather than in each transport, means every consumer sends the
 * same wire format whatever HTTP library sits underneath.
 */
export type QueryScalar = string | number | boolean | null | undefined
export type QueryValue = QueryScalar | QueryValue[] | { [key: string]: QueryValue }
export type QueryParams = { [key: string]: QueryValue }

export function serializeQuery(params: QueryParams): string {
  const parts: string[] = []

  const add = (key: string, value: QueryValue): void => {
    if (value === undefined) return
    if (value === null) {
      parts.push(`${encodeURIComponent(key)}=`)
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item) => add(`${key}[]`, item))
      return
    }
    if (typeof value === 'object') {
      Object.keys(value).forEach((child) => add(`${key}[${child}]`, value[child]))
      return
    }
    if (typeof value === 'boolean') {
      parts.push(`${encodeURIComponent(key)}=${value ? 1 : 0}`)
      return
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  }

  Object.keys(params).forEach((key) => add(key, params[key]))

  return parts.join('&')
}

/** `path` plus the serialized query, with no trailing `?` when there is nothing to send. */
export function withQuery(path: string, params?: QueryParams): string {
  if (!params) return path
  const query = serializeQuery(params)
  return query ? `${path}?${query}` : path
}
